import { z } from "zod"

import { TenantIdSchema, UserIdSchema } from "./auth.js"
import {
	DocumentIdSchema,
	MerchantChangeIdSchema,
	PartyIdSchema,
	ShipmentIdSchema,
} from "./id-schemas.js"
import { PartyRoleSchema } from "./party.js"

/**
 * MerchantChange — heist-class event capturing a Party.id swap at a
 * structural slot on a Shipment.
 *
 * Mechanical insight (PRD §7): a correction = field change within the
 * same Party.id; a heist = Party.id swap at a structural slot
 * (shipper / consignee / notify). Pointer comparison, no NLP needed.
 *
 * Five-layer detection model:
 *   L1 manual record (always available, prompt-not-auto)
 *   L2 DBL-ingest classifier with prompt (Confirm / Treat as
 *      correction / Dismiss)
 *   L3 booking-vs-DBL slot watch (catches first-DBL-already-a-heist)
 *   L4 carrier API event subscription (v2+)
 *   L5 pattern signal (deferred)
 *
 * Operator must classify each detection as HEIST, TREATED_AS_CORRECTION,
 * or DISMISSED before downstream cascades fire.
 */

const IsoDateTime = z.string().datetime()

export const MerchantChangeDetectionLayerSchema = z.enum([
	"L1_MANUAL",
	"L2_DBL_CLASSIFIER",
	"L3_SLOT_WATCH",
	"L4_API_EVENT",
	"L5_PATTERN_SIGNAL",
])
export type MerchantChangeDetectionLayer = z.infer<typeof MerchantChangeDetectionLayerSchema>

export const MerchantChangeClassificationSchema = z.enum([
	"PENDING_REVIEW",
	"HEIST",
	"TREATED_AS_CORRECTION",
	"DISMISSED",
])
export type MerchantChangeClassification = z.infer<typeof MerchantChangeClassificationSchema>

export const MerchantChangeSchema = z.object({
	id: MerchantChangeIdSchema,
	tenantId: TenantIdSchema,
	shipmentId: ShipmentIdSchema,
	documentId: DocumentIdSchema.optional(),
	partyRole: PartyRoleSchema,
	previousPartyId: PartyIdSchema,
	newPartyId: PartyIdSchema,
	detectionLayer: MerchantChangeDetectionLayerSchema,
	detectedAt: IsoDateTime,
	detectedBy: UserIdSchema.optional(),
	classification: MerchantChangeClassificationSchema,
	classifiedBy: UserIdSchema.optional(),
	classifiedAt: IsoDateTime.optional(),
	notes: z.string().max(8192).optional(),
	createdAt: IsoDateTime,
	updatedAt: IsoDateTime,
})
export type MerchantChange = z.infer<typeof MerchantChangeSchema>
